import React from 'react';
import {Box} from '@mui/material';
import {Body, designSystemToken} from '@lightricks/react-design-system';
import Sentry from '@/services/sentry/Sentry';
import {FeedItem, FEED_ITEM_TYPE} from '@/utils/aggregateActivityFeedItems';
import {formatBasedOnTimeSince} from '@/utils/formattedDate';
import getInitials from '@/utils/getInitials';
import translate from '@/utils/translate';
import {getApprovalStepDisplayName} from '@/views/campaigns/constants/approvalSteps';
import MessageCard from '@/components/message-card';
import styles from './FeedItemView.module.scss';

const AVATAR_COLOR_TOKENS = [
  'semantic.bg.brand',
  'semantic.bg.warning',
  'reference.purple.50',
] as const;

function getAvatarColor(name: string | null | undefined): string {
  if (!name) {
    return designSystemToken(AVATAR_COLOR_TOKENS[0]);
  }
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash = (hash * 31 + name.charCodeAt(i)) & 0xffffffff;
  }
  return designSystemToken(
    AVATAR_COLOR_TOKENS[Math.abs(hash) % AVATAR_COLOR_TOKENS.length]
  );
}

function EventPill({item}: {item: FeedItem}) {
  return (
    <Box className={styles.eventPill} data-testid={`feed-item-${item.type}`}>
      <Body size="sm" color={designSystemToken('semantic.fg.brand-secondary')}>
        {item.content}
      </Body>
    </Box>
  );
}

function ActionPill({item}: {item: FeedItem}) {
  return (
    <Box className={styles.eventPill} data-testid="feed-item-action">
      <Body size="xs" color={designSystemToken('semantic.fg.secondary')}>
        {item.content}
      </Body>
    </Box>
  );
}

function CommentMessageCard({item}: {item: FeedItem}) {
  const displayName = item.fullName || '';
  return (
    <Box data-testid="feed-item-comment">
      <MessageCard
        displayName={displayName}
        avatarInitials={getInitials(displayName).toUpperCase()}
        avatarBackgroundColor={getAvatarColor(item.fullName)}
        formattedDate={formatBasedOnTimeSince(new Date(item.createdAt))}
        body={item.content}
        versionBadge={translate('Version {n}', {n: item.versionNumber})}
        stepBadge={
          item.step ? getApprovalStepDisplayName(item.step) : undefined
        }
        showCampaignBadge={false}
      />
    </Box>
  );
}

function VersionMessageCard({item}: {item: FeedItem}) {
  const displayName = item.fullName || '';
  return (
    <Box data-testid="feed-item-version-message">
      <MessageCard
        displayName={displayName}
        avatarInitials={getInitials(displayName).toUpperCase()}
        avatarBackgroundColor={getAvatarColor(item.fullName)}
        formattedDate={formatBasedOnTimeSince(new Date(item.createdAt))}
        body={item.content}
        attachments={item.attachments ?? []}
        versionBadge={translate('Version {n}', {n: item.versionNumber})}
        showCampaignBadge={false}
      />
    </Box>
  );
}

function BrandFeedbackMessageCard({item}: {item: FeedItem}) {
  const displayName = item.fullName || '';
  return (
    <Box data-testid="feed-item-brand-message">
      <MessageCard
        displayName={displayName}
        avatarInitials={getInitials(displayName).toUpperCase()}
        avatarBackgroundColor={getAvatarColor(item.fullName)}
        formattedDate={formatBasedOnTimeSince(new Date(item.createdAt))}
        bodyHtml={item.brandMessageHtml ?? undefined}
        isBrand
        isBrandFeedbackToCreator={!item.isArchiveNote}
        showCampaignBadge={false}
      />
    </Box>
  );
}

const FEED_ITEM_COMPONENTS: Record<
  string,
  React.ComponentType<{item: FeedItem}>
> = {
  [FEED_ITEM_TYPE.CREATOR_UPLOAD]: EventPill,
  [FEED_ITEM_TYPE.STEP_APPROVAL]: EventPill,
  [FEED_ITEM_TYPE.ACTION]: ActionPill,
  [FEED_ITEM_TYPE.BRAND_MESSAGE]: BrandFeedbackMessageCard,
  [FEED_ITEM_TYPE.COMMENT]: CommentMessageCard,
  [FEED_ITEM_TYPE.VERSION_MESSAGE]: VersionMessageCard,
};

function FeedItemView({item}: {item: FeedItem}) {
  const Component = FEED_ITEM_COMPONENTS[item.type];
  if (!Component) {
    Sentry.captureMessage(
      `FeedItemView: unknown feed item type "${item.type}"`
    );
    return null;
  }
  return <Component item={item} />;
}

export default FeedItemView;
